import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTriggerDetailsSync, useTriggerLinesSync } from '@/hooks/useSyncMonitoringActions';
import type { OrganizationSyncStats } from '@octave/contracts';
import {
  formatExact,
  formatRelative,
  STALE_LINES_THRESHOLD_HOURS,
} from '@/lib/sync-monitoring';
import { cn } from '@/lib/utils';
import { ChevronRight, RefreshCw } from 'lucide-react';

interface SyncOrgsTableProps {
  orgs: OrganizationSyncStats[] | undefined;
  loading: boolean;
  error: boolean;
}

/**
 * Exhaustive per-org table, secondary information: collapsed by default
 * behind a discreet toggle.
 */
export function SyncOrgsTable({ orgs, loading, error }: SyncOrgsTableProps) {
  const triggerDetailsSync = useTriggerDetailsSync();
  const triggerLinesSync = useTriggerLinesSync();

  const count = orgs?.length ?? 0;

  return (
    <Collapsible>
      <CollapsibleTrigger className="group flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900">
        <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
        Toutes les organisations{loading ? '' : ` (${count})`}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3">
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead className="text-right">Sites</TableHead>
                  <TableHead className="text-right">Lignes</TableHead>
                  <TableHead>Sync orgs & sites</TableHead>
                  <TableHead>Sync lignes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-slate-400"
                    >
                      Chargement…
                    </TableCell>
                  </TableRow>
                )}
                {error && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-rose-600"
                    >
                      Erreur lors du chargement des organisations.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !error && count === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-slate-400"
                    >
                      Aucune organisation liée à Sewan.
                    </TableCell>
                  </TableRow>
                )}
                {orgs?.map((org) => {
                  const linesAgeHours = org.lastLinesSyncedAt
                    ? (Date.now() - new Date(org.lastLinesSyncedAt).getTime()) /
                      36e5
                    : Infinity;
                  const linesStale =
                    linesAgeHours > STALE_LINES_THRESHOLD_HOURS;
                  const detailsFailed = org.lastDetailsStatus === 'failure';
                  const detailsPending =
                    triggerDetailsSync.isPending &&
                    triggerDetailsSync.variables === org.organizationId;
                  const linesPending =
                    triggerLinesSync.isPending &&
                    triggerLinesSync.variables === org.organizationId;

                  return (
                    <TableRow key={org.organizationId}>
                      <TableCell>
                        <div className="font-medium">
                          {org.organizationName}
                        </div>
                        <div className="font-mono text-xs text-slate-400">
                          {org.externalOrganizationId || '—'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {org.siteCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {org.totalLines}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{formatRelative(org.lastDetailsSyncedAt)}</span>
                          {detailsFailed && (
                            <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">
                              KO
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-400">
                          {formatExact(org.lastDetailsSyncedAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{formatRelative(org.lastLinesSyncedAt)}</span>
                          {org.totalLines === 0 ? (
                            <Badge variant="secondary">Vide</Badge>
                          ) : linesStale ? (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                              Stale
                            </Badge>
                          ) : null}
                        </div>
                        <div className="text-xs text-slate-400">
                          {formatExact(org.lastLinesSyncedAt)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={
                              !org.externalOrganizationId || detailsPending
                            }
                            onClick={() =>
                              triggerDetailsSync.mutate(org.organizationId)
                            }
                          >
                            <RefreshCw
                              className={cn(
                                'mr-1.5 h-3.5 w-3.5',
                                detailsPending && 'animate-spin'
                              )}
                            />
                            Orgs & sites
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!org.externalOrganizationId || linesPending}
                            onClick={() =>
                              triggerLinesSync.mutate(org.organizationId)
                            }
                          >
                            <RefreshCw
                              className={cn(
                                'mr-1.5 h-3.5 w-3.5',
                                linesPending && 'animate-spin'
                              )}
                            />
                            Lignes
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}
